import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Card as MuiCard,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  TableHead
} from '@mui/material';
import { Close, Edit, ExpandMore } from '@mui/icons-material';
import { motion } from 'framer-motion';

const ViewDetails = ({ open, onClose, item, onEdit, categories }) => {
  const { t } = useTranslation();
  // Use motion.div instead of motion(Box)
  const MotionBox = motion.div;

  if (!item) return null;

  const getCategoryName = (categoryId) => {
    if (!categories || !categoryId) return t('menu.uncategorized');
    const category = categories.find(c => c.category_id === parseInt(categoryId));
    return category ? category.name : t('menu.uncategorized');
  };

  // Check if variations exist and are not empty
  const hasVariations = item.variations && Array.isArray(item.variations) && item.variations.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('menu.itemDetailsTitle')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <MuiCard sx={{ height: '100%', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      
                      <Chip 
                        label={item.available ? t('status.available') : t('status.unavailable')} 
                        color={item.available ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                              {t('menu.category')}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Chip 
                                label={getCategoryName(item.category_id)} 
                                size="small" 
                                sx={{ borderRadius: 1 }}
                              />
                            </TableCell>
                          </TableRow>
                          
                          <TableRow>
                            <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                              {t('menu.price')}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                                ${parseFloat(item.price).toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          
                          {item.cost && (
                            <TableRow>
                              <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                                {t('menu.cost')}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                ${parseFloat(item.cost).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          )}
                          
                          <TableRow>
                            <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                              {t('menu.itemId')}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {item.item_id}
                            </TableCell>
                          </TableRow>
                          
                          {item.created_at && (
                            <TableRow>
                              <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                                {t('menu.created')}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {new Date(item.created_at).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )}
                          
                          {item.modified_at && (
                            <TableRow>
                              <TableCell component="th" sx={{ width: '40%', py: 1.5, fontWeight: 500 }}>
                                {t('menu.lastUpdated')}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                {new Date(item.modified_at).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('menu.description')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {item.description || t('menu.noDescription')}
                    </Typography>
                    
                    {hasVariations && (
                      <Box sx={{ mt: 3 }}>
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {t('menu.variations')} ({item.variations.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('menu.variationName')}</TableCell>
                                    <TableCell align="right">{t('menu.price')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {item.variations.map((variation, index) => (
                                    <TableRow key={variation.variation_id || index}>
                                      <TableCell>{variation.name}</TableCell>
                                      <TableCell align="right">
                                        ${parseFloat(variation.price).toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </MuiCard>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <MotionBox
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <MuiCard sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 0 }}>
                  {item.image ? (
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.name}
                      sx={{
                        width: '100%',
                        height: 250,
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 250,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'action.hover',
                        color: 'text.secondary'
                      }}
                    >
                      <Typography variant="body1">{t('menu.noImageAvailable')}</Typography>
                    </Box>
                  )}
                </CardContent>
              </MuiCard>
              
              {/* Quick Actions */}
              <MuiCard sx={{ mt: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('menu.quickActions')}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Edit />}
                    onClick={() => {
                      onClose();
                      if (onEdit) onEdit(item);
                    }}
                    sx={{ 
                      mb: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                      }
                    }}
                  >
                    {t('menu.editItem')}
                  </Button>
                </CardContent>
              </MuiCard>
            </MotionBox>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDetails;